/**
 * Risk factors, tools (EPI) and licences of a qualification as the slide-over
 * edits them, with the marks of what the AI proposed, and their payload.
 * Same shape as the job description: conditions { position_at_risk,
 * penibilite { <besttField>: Boolean } }, tools [name], licences [name].
 */

import { normalizePenibilite } from 'components/SlideOver/dataSlideOver/data/slideOverAddMission/missionTemplate/templateFormData';

export const REFERENTIAL_STATUS = {
  LOADING: 'loading',
  READY: 'ready',
  FAILED: 'failed',
};

const isPlainObject = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/** The riskypositions referential: valid entries only, in their `order`. */
export const sortRiskyPositions = (response) =>
  (Array.isArray(response) ? response : [])
    .filter((item) => typeof item?.besttField === 'string' && item.besttField)
    .slice()
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) -
        (b.order ?? Number.MAX_SAFE_INTEGER),
    );

const cleanName = (name) => (typeof name === 'string' ? name.trim() : '');

/** Same name for the api: edge spaces and case aside. */
export const requirementNameKey = (name) =>
  cleanName(name).toLocaleLowerCase('fr');

const uniqueNames = (names) => {
  const seen = new Set();
  return (Array.isArray(names) ? names : []).reduce((unique, name) => {
    const cleaned = cleanName(name);
    const key = requirementNameKey(cleaned);
    if (!cleaned || seen.has(key)) return unique;
    seen.add(key);
    unique.push(cleaned);
    return unique;
  }, []);
};

/** The names of the tools or licence collection, as offered in the pickers. */
export const toOptionNames = (response) =>
  uniqueNames(
    (Array.isArray(response) ? response : []).map((item) => item?.name),
  );

// ---- Risk ----------------------------------------------------------------

export const emptyRisk = () => ({
  positionAtRisk: false,
  penibilite: {},
  isSwitchAi: false,
  aiFactors: [],
});

/** Stored conditions; a qualification without them is not at risk. */
export const toFormRisk = (conditions) => {
  const positionAtRisk = conditions?.position_at_risk === true;
  return {
    positionAtRisk,
    penibilite: positionAtRisk
      ? normalizePenibilite(conditions?.penibilite)
      : {},
    isSwitchAi: false,
    aiFactors: [],
  };
};

/** The AI's conditions: the switch and every checked factor marked as proposed. */
export const toSuggestedRisk = (conditions) => {
  const risk = toFormRisk(conditions);
  return {
    ...risk,
    isSwitchAi: true,
    aiFactors: Object.keys(risk.penibilite),
  };
};

/** The switch, set by the admin: "no" unchecks every factor. */
export const withPositionAtRisk = (risk, positionAtRisk) => ({
  positionAtRisk,
  penibilite: positionAtRisk ? risk.penibilite : {},
  isSwitchAi: false,
  aiFactors: positionAtRisk ? risk.aiFactors : [],
});

/** A factor the admin checks or unchecks is theirs from then on. */
export const toggleRiskFactor = (risk, besttField) => {
  const penibilite = { ...risk.penibilite };
  if (penibilite[besttField]) delete penibilite[besttField];
  else penibilite[besttField] = true;
  return {
    ...risk,
    penibilite,
    aiFactors: risk.aiFactors.filter((field) => field !== besttField),
  };
};

export const isAiRiskFactor = (risk, besttField) =>
  Boolean(risk.penibilite[besttField]) && risk.aiFactors.includes(besttField);

/** Same rule as the job description: at risk means at least one factor. */
export const isRiskFactorMissing = (risk) =>
  risk.positionAtRisk && Object.keys(risk.penibilite).length === 0;

/**
 * Every factor of the referential as a boolean, plus a checked one the
 * referential no longer lists, so the api can name it rather than lose it.
 */
export const buildConditionsPayload = (risk, riskyPositions = []) => {
  const fields = new Set([
    ...riskyPositions.map((position) => position.besttField),
    ...Object.keys(risk.penibilite),
  ]);
  const penibilite = {};
  fields.forEach((field) => {
    penibilite[field] = risk.positionAtRisk && risk.penibilite[field] === true;
  });
  return { position_at_risk: risk.positionAtRisk, penibilite };
};

export const isConditionsValue = isPlainObject;

// ---- Tools and licences --------------------------------------------------

const toItems = (names, source) =>
  uniqueNames(names).map((name) => ({ name, source }));

/** Stored names: the admin's. */
export const toFormItems = (names) => toItems(names, 'admin');

/** Names proposed by the AI, marked as such until the admin removes them. */
export const toSuggestedItems = (names) => toItems(names, 'ai');

export const hasRequirementItem = (items, name) =>
  items.some(
    (item) => requirementNameKey(item.name) === requirementNameKey(name),
  );

/** Checking a name in the picker adds it as the admin's; unchecking removes it. */
export const toggleRequirementItem = (items, name) => {
  if (hasRequirementItem(items, name))
    return removeRequirementItem(items, name);
  const cleaned = cleanName(name);
  return cleaned ? [...items, { name: cleaned, source: 'admin' }] : items;
};

export const removeRequirementItem = (items, name) =>
  items.filter(
    (item) => requirementNameKey(item.name) !== requirementNameKey(name),
  );

export const buildNamesPayload = (items) => items.map((item) => item.name);

// ---- Api refusals --------------------------------------------------------

// The api names the refused value next to { error, errorCode }; the key is
// read loosely so a renamed field still shows the name.
const INVALID_NAME_KEYS = [
  'name',
  'value',
  'invalidName',
  'invalidValue',
  'invalid',
  'tool',
  'licence',
  'factor',
  'besttField',
];

/** The value an api refusal names, or '' if it names none. */
export const readInvalidName = (data) => {
  if (!isPlainObject(data)) return '';
  for (const key of INVALID_NAME_KEYS) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (Array.isArray(value)) {
      const names = value.filter(
        (item) => typeof item === 'string' && item.trim(),
      );
      if (names.length > 0) return names.join(', ');
    }
  }
  return '';
};
